import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PencilSimpleLine, Sparkle, PaperPlaneTilt, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function ProspectPicker({ value, onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get(`/outreach/prospects/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (value) return (
    <div className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm" data-testid="compose-picked-prospect">
      <span><b>{value.property_name}</b>{[value.city, value.status].filter(Boolean).map((x) => ` · ${x}`).join("")}</span>
      <button className="text-xs underline text-stone" onClick={() => onPick(null)} data-testid="compose-unpick">cambia</button>
    </div>
  );
  return (
    <div className="relative">
      <Input placeholder="Cerca prospect (nome struttura, email, città) — opzionale" value={q} onChange={(e) => setQ(e.target.value)} data-testid="compose-prospect-search" />
      {!!res.length && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-line bg-canvas shadow-lg max-h-56 overflow-auto">
          {res.map((p) => (
            <button key={p.id} className="w-full text-left px-3 py-2 text-sm hover:bg-sand/60" onClick={() => { onPick(p); setQ(""); }} data-testid={`compose-prospect-opt-${p.id}`}>
              <b>{p.property_name}</b> <span className="text-stone">· {p.email} · {p.city || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ComposeEmail({ initialProspect = null, trigger }) {
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(initialProspect);
  const empty = { to: "", cc: "", subject: "", body: "", property_name: "", instructions: "", confidential: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const pick = (x) => { setP(x); setF((s) => ({ ...s, to: x?.email || "", confidential: x?.property_type === "partner" || s.confidential })); };
  useEffect(() => { if (open && initialProspect) pick(initialProspect); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const aiDraft = async () => {
    if (!f.instructions.trim()) return toast.error("Scrivi cosa vuoi dire in 1 riga");
    setBusy(true);
    try {
      const r = await api.post("/outreach/compose/draft", { instructions: f.instructions, prospect_id: p?.id, to: f.to.split(/[,;\s]+/)[0] });
      setF((s) => ({ ...s, subject: r.data.subject || s.subject, body: r.data.body }));
    } catch { toast.error("Errore AI"); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/outreach/compose", { to: f.to, cc: f.cc, subject: f.subject, body: f.body, prospect_id: p?.id, property_name: f.property_name || undefined, confidential: f.confidential });
      toast.success("Email inviata e salvata nello storico");
      setOpen(false); setF(empty); setP(initialProspect);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); } finally { setBusy(false); }
  };

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger || (
        <Button className="rounded-full bg-gold text-ink hover:bg-canvas" data-testid="compose-open-btn"><PencilSimpleLine size={16} className="mr-1" />Scrivi email</Button>
      )}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-canvas max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="compose-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Nuova email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <ProspectPicker value={p} onPick={pick} />
            <Input placeholder="A: uno o più indirizzi, separati da virgola" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="compose-to" />
            <Input placeholder="Cc: per conoscenza (facoltativo, separati da virgola)" value={f.cc} onChange={(e) => setF({ ...f, cc: e.target.value })} data-testid="compose-cc" />
            {!p && <Input placeholder="Nome struttura (per salvarla in Cold Outreach)" value={f.property_name} onChange={(e) => setF({ ...f, property_name: e.target.value })} data-testid="compose-property-name" />}
            <div className="flex gap-2">
              <Input placeholder="Aiuto AI: es. 'rispondi che va bene la demo giovedì alle 15'" value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} data-testid="compose-ai-instructions" />
              <Button variant="outline" className="rounded-full whitespace-nowrap" onClick={aiDraft} disabled={busy} data-testid="compose-ai-btn"><Sparkle size={14} className="mr-1" />Bozza AI</Button>
            </div>
            <Input placeholder="Oggetto" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="compose-subject" />
            <Textarea rows={10} placeholder="Testo dell'email" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="compose-body" />
            <label className="flex items-start gap-3 rounded-lg border border-line p-3 text-sm cursor-pointer" data-testid="compose-confidential">
              <Switch checked={f.confidential} onCheckedChange={(v) => setF({ ...f, confidential: v })} data-testid="compose-confidential-switch" />
              <span><b>Nota di riservatezza e tutela legale</b><br /><span className="text-stone text-xs">Aggiunge in fondo all'email la clausola su segreti commerciali, concorrenza sleale e diritto d'autore. Consigliata per partner e documenti riservati.</span></span>
            </label>
            <div className="flex justify-end">
              <Button onClick={send} disabled={busy || !f.to || !f.subject || !f.body} className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="compose-send-btn">
                <PaperPlaneTilt size={16} className="mr-1" />{busy ? "Attendi…" : "Invia"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ThreadButton({ prospectId }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  useEffect(() => { if (open) api.get(`/outreach/thread/${prospectId}`).then((r) => setD(r.data)).catch(() => toast.error("Storico non disponibile")); }, [open, prospectId]);
  if (!prospectId) return null;
  return (
    <>
      <Button variant="outline" className="rounded-full border-line h-9 text-xs" onClick={() => setOpen(true)} data-testid="thread-open-btn"><ClockCounterClockwise size={14} className="mr-1" />Storico conversazione</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-canvas max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="thread-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{d?.prospect?.property_name || "Storico"}</DialogTitle></DialogHeader>
          {d && <div className="text-xs text-stone">{[d.prospect.email, ...(d.prospect.alt_emails || [])].join(" · ")}</div>}
          <div className="space-y-3 mt-2">
            {(d?.items || []).map((it, i) => (
              <div key={i} className={`rounded-xl p-3 text-sm ${it.dir === "out" ? "bg-sand/60 ml-8" : "bg-olive/10 mr-8"}`} data-testid={`thread-item-${i}`}>
                <div className="text-[11px] uppercase tracking-widest text-stone">{it.dir === "out" ? `Inviata → ${it.to || ""}` : `Ricevuta ← ${it.from || ""}`} · {fmt(it.at)}</div>
                <div className="font-medium text-ink mt-1">{it.subject}</div>
                <div className="whitespace-pre-line text-ink/80 mt-1">{it.body}</div>
              </div>
            ))}
            {d && !d.items.length && <div className="text-sm text-stone">Nessuna email ancora.</div>}
          </div>
          {d && <ComposeEmail initialProspect={d.prospect} trigger={<Button className="rounded-full bg-ink text-canvas mt-3" data-testid="thread-compose-btn">Scrivi a questo prospect</Button>} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
